import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardBody, Button, Spinner, Chip, Breadcrumbs, BreadcrumbItem } from '@heroui/react';
import {
  Tree,
  ArrowLeft,
  Shuffle,
  Database,
  Hash,
} from '@phosphor-icons/react';
import { useEntityVisualization } from './hooks/useEntityVisualization';
import EntityTreeView from './components/EntityTreeView';
import PropertySearch from './components/PropertySearch';
import EntitySearchPanel from './components/EntitySearchPanel';

const EntitySchemaVisualization: React.FC = () => {
  const { entityName } = useParams<{ entityName: string }>();
  const navigate = useNavigate();

  const {
    entity,
    loading,
    error,
    totalCount,
    searchQuery,
    setSearchQuery,
    fetchDifferentEntity,
    matchCount,
    totalPropertyCount,
    // Related field search
    searchableDirectFields,
    searchableRelations,
    searchByRelatedField,
    isSearching,
    activeRelatedSearch,
    searchResults,
    searchResultIndex,
    totalSearchMatches,
    nextSearchResult,
    previousSearchResult,
    clearRelatedSearch,
  } = useEntityVisualization(entityName || '');

  // State for search navigation
  const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(0);
  const [matchRefs, setMatchRefs] = useState<Array<{ref: HTMLDivElement | null, relationName?: string}>>([]);

  // Reset match index when search query changes
  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchQuery, entity]);

  // Callback to register match elements
  const registerMatchRef = useCallback((ref: HTMLDivElement | null, relationName?: string) => {
    if (ref) {
      setMatchRefs(prev => {
        // Avoid duplicates by checking if ref already exists
        const exists = prev.some(item => item.ref === ref);
        if (exists) return prev;

        console.log('🔖 Registering match ref:', relationName || 'property');
        return [...prev, {ref, relationName}];
      });
    }
  }, []);

  // Clear refs only when entity changes (not on search query change)
  useEffect(() => {
    setMatchRefs([]);
  }, [entity]);

  // Debug: Log when match refs are collected
  useEffect(() => {
    console.log('📊 Match refs collected:', matchRefs.length);
  }, [matchRefs]);

  // Auto-scroll to first match when search query changes and refs are ready
  useEffect(() => {
    if (searchQuery && matchRefs.length > 0 && currentMatchIndex === 0) {
      const firstMatch = matchRefs[0];
      if (firstMatch && firstMatch.ref) {
        // Small delay to ensure rendering is complete
        setTimeout(() => {
          console.log('🎯 Auto-scrolling to first match');
          firstMatch.ref?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [searchQuery, matchRefs.length, currentMatchIndex]);

  // Navigate to previous match
  const navigateToPrevious = useCallback(() => {
    if (matchRefs.length === 0) {
      console.log('⚠️ No match refs available for navigation');
      return;
    }

    const newIndex = currentMatchIndex === 0 ? matchRefs.length - 1 : currentMatchIndex - 1;
    console.log(`⬅️ Navigating to previous match: ${newIndex + 1} of ${matchRefs.length}`);
    setCurrentMatchIndex(newIndex);

    const matchRef = matchRefs[newIndex];
    if (matchRef?.ref) {
      matchRef.ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentMatchIndex, matchRefs]);

  // Navigate to next match
  const navigateToNext = useCallback(() => {
    if (matchRefs.length === 0) {
      console.log('⚠️ No match refs available for navigation');
      return;
    }

    const newIndex = currentMatchIndex === matchRefs.length - 1 ? 0 : currentMatchIndex + 1;
    console.log(`➡️ Navigating to next match: ${newIndex + 1} of ${matchRefs.length}`);
    setCurrentMatchIndex(newIndex);

    const matchRef = matchRefs[newIndex];
    if (matchRef?.ref) {
      matchRef.ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentMatchIndex, matchRefs]);

  if (!entityName) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="bg-red-50">
          <CardBody>
            <p className="text-red-600">Geen entiteitnaam opgegeven</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 min-h-screen">
      {/* Breadcrumbs */}
      <Breadcrumbs>
        <BreadcrumbItem
          startContent={<Database size={18} />}
          onClick={() => navigate('/settings#documentTemplates')}
          className="cursor-pointer"
        >
          Instellingen
        </BreadcrumbItem>
        <BreadcrumbItem
          startContent={<Tree size={18} />}
        >
          {entityName}
        </BreadcrumbItem>
      </Breadcrumbs>

      {/* Header Card */}
      <Card className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg">
        <CardBody className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                isIconOnly
                variant="light"
                onPress={() => navigate(-1)}
                className="text-white"
                size="lg"
              >
                <ArrowLeft size={24} weight="bold" />
              </Button>

              <div>
                <h1 className="text-3xl font-bold flex items-center gap-3">
                  <Tree size={32} weight="duotone" />
                  {entityName}
                </h1>
                <p className="text-purple-100 mt-1">
                  Entity Schema Visualisatie
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {totalCount > 0 && (
                <Chip
                  size="lg"
                  variant="flat"
                  startContent={<Hash size={18} />}
                  className="bg-white/20 text-white"
                >
                  {totalCount} entiteiten in database
                </Chip>
              )}

              <Button
                color="secondary"
                variant="shadow"
                startContent={<Shuffle size={20} />}
                onPress={fetchDifferentEntity}
                isDisabled={loading || isSearching || totalCount === 0}
                size="lg"
              >
                Andere Entiteit
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Related Field Search Panel */}
      {(searchableDirectFields.length > 0 || searchableRelations.length > 0) && (
        <div className="mt-4">
          <EntitySearchPanel
            tableName={entityName}
            searchableDirectFields={searchableDirectFields}
            searchableRelations={searchableRelations}
            onSearch={searchByRelatedField}
            onClearSearch={clearRelatedSearch}
            isSearching={isSearching}
            activeSearch={activeRelatedSearch}
            searchResultCount={searchResults.length}
            totalSearchMatches={totalSearchMatches}
            currentResultIndex={searchResultIndex}
            onPrevious={previousSearchResult}
            onNext={nextSearchResult}
          />
        </div>
      )}

      {/* Search Bar - Sticky */}
      <div className="sticky top-0 z-50 bg-white shadow-md -mx-4 px-4 py-2 mb-6">
        <PropertySearch
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          matchCount={matchCount}
          totalCount={totalPropertyCount}
          currentMatchIndex={currentMatchIndex}
          onNavigateToPrevious={navigateToPrevious}
          onNavigateToNext={navigateToNext}
        />
      </div>

      {/* Content with proper spacing */}
      <div className="space-y-6">
      {/* Loading State */}
      {(loading || isSearching) && (
        <Card>
          <CardBody className="flex items-center justify-center py-16">
            <Spinner size="lg" color="secondary" />
            <p className="mt-4 text-gray-600">
              {isSearching ? 'Zoeken naar entiteiten...' : 'Entiteit laden...'}
            </p>
          </CardBody>
        </Card>
      )}

      {/* Error State */}
      {error && !loading && (
        <Card className="bg-red-50 border border-red-200">
          <CardBody className="p-6">
            <h3 className="text-red-800 font-semibold mb-2">Fout bij laden</h3>
            <p className="text-red-600">{error}</p>
            <Button
              color="danger"
              variant="flat"
              onPress={() => fetchDifferentEntity()}
              className="mt-4"
            >
              Probeer opnieuw
            </Button>
          </CardBody>
        </Card>
      )}

      {/* Entity Display */}
      {entity && !loading && !isSearching && (
        <>
          {/* Entity Info Card */}
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
            <CardBody className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 mb-2">
                    Huidige Entiteit
                  </h2>
                  <div className="flex items-center gap-2">
                    <Chip size="sm" variant="flat" color="primary">
                      Tabel: {entity.tableName}
                    </Chip>
                    <Chip size="sm" variant="flat" color="secondary">
                      ID: {String(entity.primaryKey).substring(0, 20)}
                      {String(entity.primaryKey).length > 20 && '...'}
                    </Chip>
                    <Chip size="sm" variant="flat" color="success">
                      {Object.keys(entity.properties || {}).length} eigenschappen
                    </Chip>
                    <Chip size="sm" variant="flat" color="warning">
                      {entity.relations?.length || 0} relaties
                    </Chip>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Entity Tree View */}
          <EntityTreeView
            entity={entity}
            searchQuery={searchQuery}
            registerMatchRef={registerMatchRef}
            currentMatchIndex={currentMatchIndex}
          />
        </>
      )}

      {/* Empty State */}
      {!entity && !loading && !error && totalCount === 0 && (
        <Card className="bg-gray-50">
          <CardBody className="text-center py-16">
            <Database size={64} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Geen entiteiten gevonden
            </h3>
            <p className="text-gray-500">
              De tabel "{entityName}" bevat geen data.
            </p>
          </CardBody>
        </Card>
      )}
      </div> {/* Close space-y-6 content wrapper */}
    </div>
  );
};

export default EntitySchemaVisualization;
